import useDateFormatters from "@hooks/useDateFormatters"

import { faIcon } from "@utils/faIcon"

import { Avatar } from "@atoms"

import {
    BillPaymentBadge,
    IssueDateBadge,
    LabelsBadges,
    LastModifiedBadge,
    TermsBadge,
    TotalBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { BillCardProps } from "./BillCard.types"

const vendorIcon = faIcon({ name: "store", style: "solid" })

type BillTimestampKey =
    | "created_at"
    | "last_received_at"
    | "last_paid_at"
    | "last_voided_at"
    | "last_status_transition_time"

const STATUS_TRANSITION: Record<Bill["status_label"], { verb: string; timestampKey: BillTimestampKey }> = {
    Draft: { verb: "Created", timestampKey: "created_at" },
    Unsent: { verb: "Created", timestampKey: "created_at" },
    Outstanding: { verb: "Received", timestampKey: "last_received_at" },
    Overdue: { verb: "Received", timestampKey: "last_received_at" },
    Paid: { verb: "Paid", timestampKey: "last_paid_at" },
    Voided: { verb: "Voided", timestampKey: "last_voided_at" },
}

// The payment status badge only applies once a bill is in a billable lifecycle.
function isPayable(status: Bill["status_label"]): boolean {
    return status === "Paid" || status === "Outstanding" || status === "Overdue"
}

// Terms only run overdue while the bill is open; `is_overdue` is date-based alone, so a closed bill past its
// due date would otherwise show the overdue look.
const OPEN_STATUS_LABELS: Bill["status_label"][] = ["Draft", "Unsent", "Outstanding", "Overdue"]

export default function BillCard(props: BillCardProps) {
    const { bill, displayedAttributes, onClick, isLoading = false, isTabbable, className, ...errorProps } = props

    const { formatDatetimeTooltip } = useDateFormatters()

    const { verb, timestampKey } = STATUS_TRANSITION[bill.status_label]
    const statusTooltip = formatDatetimeTooltip(
        verb,
        bill[timestampKey] ?? bill.last_status_transition_time ?? bill.created_at,
    )

    const showSubtotal = shouldShowAttribute(displayedAttributes, "subtotal") && bill.subtotal != null
    const showIssued = shouldShowAttribute(displayedAttributes, "date_issued") && Boolean(bill.date_issued)
    const showTerms =
        shouldShowAttribute(displayedAttributes, "terms") && Boolean(bill.date_issued) && Boolean(bill.date_due)
    const showPaymentStatus =
        shouldShowAttribute(displayedAttributes, "payment_status") && isPayable(bill.status_label)
    const showLastModified =
        shouldShowAttribute(displayedAttributes, "last_modified_at") && Boolean(bill.last_modified_at)
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && bill.labels.length > 0

    const hasAttributes =
        showSubtotal || showIssued || showTerms || showPaymentStatus || showLastModified || showLabels

    return (
        <ObjectCard
            onClick={onClick}
            isLoading={isLoading}
            isTabbable={isTabbable}
            className={className}
            {...errorProps}
        >
            <ObjectCard.Header
                id={bill.custom_id ?? String(bill.id)}
                subId={bill.vendor_invoice_id || undefined}
                subIdLabel="Vendor invoice ID"
                statusBadge={
                    <ObjectStatusBadge
                        objectType="bill"
                        status={bill.status_label}
                        size="md"
                        tooltip={statusTooltip}
                    />
                }
            />
            <ObjectCard.Body
                avatar={<Avatar variant="object" icon={vendorIcon} size="xl" />}
                primaryText={bill.vendor_name}
                secondaryText={bill.billing_address_formatted ?? undefined}
                secondaryTextPlaceholder="No billing address"
                secondaryTextTooltip={bill.billing_address_formatted_multiline ?? undefined}
            />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showSubtotal && <TotalBadge value={bill.subtotal} label="Subtotal" />}
                    {showIssued && <IssueDateBadge date={bill.date_issued} />}
                    {showTerms && (
                        <TermsBadge
                            variant="dates"
                            dateIssued={bill.date_issued}
                            dateDue={bill.date_due}
                            isExpired={bill.is_overdue && OPEN_STATUS_LABELS.includes(bill.status_label)}
                            label="Due"
                            expiredLabel="Due"
                        />
                    )}
                    {showPaymentStatus && (
                        <BillPaymentBadge
                            status={bill.status_label}
                            isOverdue={bill.is_overdue}
                            total={bill.subtotal}
                            amountPaid={bill.amount_paid}
                            amountCredited={bill.amount_credited}
                            amountDue={bill.amount_due}
                        />
                    )}
                    {showLastModified && <LastModifiedBadge date={bill.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={bill.labels} />}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
